import { GridColDef } from '@mui/x-data-grid-pro';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import EDCheckbox from '../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDDialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import EDDataGrid from '../../../../../../../BaseComponents/XGrid/EDDataGrid';
import {
  TEDEKBokforderRadPERIODISERADEnum,
  TEDEKBokforderVariantEnum,
  TEDEKKontodelREDOVISN_TYPEnum,
} from '../../../../../../../globalQueryTypes';
import { findFormatter } from '../../../../../../../Utils/Formatters/FindFormatter';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { FormUpdateAssociatonType } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import KonteringContext from '../../../../../_Common/Components/KonteringManager/KonteringContext';
import {
  BokforderQuery_node_TEDEKBokforder__Associations_Rader,
  BokforderQuery_node_TEDEKBokforder__Associations_Rader__Associations,
} from '../../Queries/_QueryTypes/BokforderQuery';
import {
  createKodstrangDelarFromRow,
  isRowEmpty,
} from '../../Utils/BokforderUtils';
import BokforderContext from '../BokforderContext/BokforderContext';
import { RowComponentWithTooltip } from '../BokforderCreateDialog/Components/BokforderVerificationWizardStep2/BokforderVerificationWizardStep2';
import { onDateBlur } from './Utils/BokforderPeriodDialogUtils';
import BokforderAreYouSureDialog from '../BokforderAreYouSureDialog/BokforderAreYouSureDialog';
import { NEWLY_ADDED_ID_PREFIX_FOR_UPDATE } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Const/BusinessDataFormConst';
import BokforderPeriodDateHandler, {
  IDateInputValue,
} from './Components/BokforderPeriodDateHandler/BokforderPeriodDateHandler';
import useCantEditPeriod from '../../Hooks/useCantEditPeriod';

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const currFormatter = findFormatter('TEDFormatCurr');

const BokforderPeriodDialog: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { setOpen } = props;
  const bokforderContext = useContext(BokforderContext);
  const formContext = useContext(BusinessDataFormContext);
  const formContextUpdate = formContext?.update;
  const formContextDispatch = formContext?.formStateDispatch;
  const parentFormContextDispatch =
    formContext?.parentFormContext?.formStateDispatch;
  const konteringContext = useContext(KonteringContext);
  const getKontodelDefsByKodplanId =
    konteringContext.konteringUtils.getKontodelDefsByKodplanId;
  const [chosenRows, setChosenRows] = useState<
    BokforderQuery_node_TEDEKBokforder__Associations_Rader[]
  >([]);
  const [fromDateInputValue, setFromDateInputValue] = useState<IDateInputValue>(
    { value: '', isValid: false }
  );
  const [toDateInputValue, setToDateInputValue] = useState<IDateInputValue>({
    value: '',
    isValid: false,
  });
  const [areYouSureDialogOpen, setAreYouSureDialogOpen] =
    useState<boolean>(false);
  const [dialogYesCallback, setDialogYesCallback] = useState({
    callback: () => {},
  });
  const [infoSummeringDialogOpen, setInfoSummeringDialogOpen] =
    useState<boolean>(false);

  const [
    infoDiffMoreThanOneYearDialogOpen,
    setInfoDiffMoreThanOneYearDialogOpen,
  ] = useState<boolean>(false);

  const kontodeldefsFromKodplan = useMemo(
    () =>
      konteringContext.konteringValidationQueryLoading
        ? []
        : getKontodelDefsByKodplanId(
            bokforderContext.bokforderKodplan?.id ?? ''
          ),
    [
      bokforderContext.bokforderKodplan?.id,
      getKontodelDefsByKodplanId,
      konteringContext.konteringValidationQueryLoading,
    ]
  );

  const allFilledRows: BokforderQuery_node_TEDEKBokforder__Associations_Rader[] =
    useMemo(
      () =>
        formContext?.formState.currentValues?._Associations?.Rader?.filter(
          (row: BokforderQuery_node_TEDEKBokforder__Associations_Rader) =>
            !isRowEmpty(row)
        ) ?? [],
      [formContext?.formState.currentValues?._Associations?.Rader]
    );

  const getRowDisabledReason = useCallback(
    (row: BokforderQuery_node_TEDEKBokforder__Associations_Rader) => {
      const errorMessages = [];
      if (konteringContext.konteringValidationQueryLoading) {
        errorMessages.push('Kodplan hämtas');
      }

      const baskonto = kontodeldefsFromKodplan
        .find(def => def.ordningsNr === 1)
        ?.kontodelar.find(
          kontodel => kontodel.id === row._Associations?.Kontodel1?.id
        );

      if (
        baskonto?.redovisningsTyp ===
        TEDEKKontodelREDOVISN_TYPEnum.STALLNINGSKONTO
      ) {
        errorMessages.push('Balanskonton kan inte periodiseras');
      }

      if (
        row.REF_LOPNR &&
        bokforderContext.bokforderVariant ===
          TEDEKBokforderVariantEnum.PERIODISERA
      ) {
        errorMessages.push('Vända rader kan inte periodiseras');
      }
      return errorMessages;
    },
    [
      bokforderContext.bokforderVariant,
      konteringContext.konteringValidationQueryLoading,
      kontodeldefsFromKodplan,
    ]
  );

  const availableRows = useMemo(
    () => allFilledRows.filter(row => getRowDisabledReason(row).length === 0),
    [allFilledRows, getRowDisabledReason]
  );

  const resetStates = useCallback(() => {
    setChosenRows([]);
    setFromDateInputValue({ value: '', isValid: false });
    setToDateInputValue({ value: '', isValid: false });
  }, []);

  const onCheckboxClick = useCallback(
    (rowID: string) => (isChecked: boolean) => {
      if (isChecked) {
        const addedRow = availableRows.find(row => row.id === rowID.toString());

        if (addedRow) {
          setChosenRows([...chosenRows, addedRow]);
        }
      } else {
        const newRows = chosenRows.filter(row => row.id !== rowID.toString());
        setChosenRows([...newRows]);
      }
    },
    [chosenRows, setChosenRows, availableRows]
  );

  const columns: GridColDef[] = useMemo(() => {
    const tempColumns: GridColDef[] = [
      {
        align: 'center',
        field: 'Vald',
        renderCell: params => {
          const disabled = getRowDisabledReason(params.row).length > 0;

          return (
            <EDCheckbox
              checked={chosenRows.some(row => row.id === params.id.toString())}
              disabled={disabled}
              onCheck={onCheckboxClick(params.id.toString())}
            />
          );
        },
        minWidth: 46,
      },
      {
        field: 'Kontostrang',
        headerName: 'Kodsträng',
        align: undefined,
        width: 150,
        valueGetter: (_, row) => {
          if (row?.Kontostrang) {
            return row.Kontostrang;
          }

          const kodStrang = createKodstrangDelarFromRow(
            row?._Associations as BokforderQuery_node_TEDEKBokforder__Associations_Rader__Associations,
            kontodeldefsFromKodplan
          );

          const kodstrangString = kodStrang.reduce((prev, curr, index) => {
            if (curr.kontodel) {
              return index === 0 ? curr.kontodel : prev + '-' + curr.kontodel;
            } else {
              return prev;
            }
          }, '');

          return kodstrangString;
        },
      },
      {
        field: 'Kontoben',
        headerName: 'Benämning',
        align: undefined,
        width: 480,
        valueGetter: (_, row) => {
          const kodStrang = createKodstrangDelarFromRow(
            row?._Associations as BokforderQuery_node_TEDEKBokforder__Associations_Rader__Associations,
            kontodeldefsFromKodplan
          );
          return row?.Kontoben
            ? row.Kontoben
            : kodStrang.reduce((prev, curr, index) => {
                if (curr.benamning) {
                  return index === 0
                    ? curr.benamning
                    : prev + '-' + curr.benamning;
                } else {
                  return prev;
                }
              }, '');
        },
      },
      {
        field: 'DEBET',
        align: 'right',
        headerAlign: 'right',
        headerName: 'Debet',
        renderCell: params => {
          return currFormatter.displayer.display(
            params.row?.DEBET,
            true,
            'DisplayZeroAsBlank="Y"'
          );
        },
      },
      {
        field: 'KREDIT',
        align: 'right',
        headerAlign: 'right',
        headerName: 'Kredit',
        renderCell: params => {
          return currFormatter.displayer.display(
            params.row?.KREDIT,
            true,
            'DisplayZeroAsBlank="Y"'
          );
        },
      },
      {
        field: 'PERIODISERAD_FROM_DATUM',
        headerName: 'Period, From',
        width: 120,
      },
      {
        field: 'PERIODISERAD_TOM_DATUM',
        headerName: 'Period, Tom',
        width: 120,
      },
    ];

    return tempColumns;
  }, [
    chosenRows,
    getRowDisabledReason,
    kontodeldefsFromKodplan,
    onCheckboxClick,
  ]);

  const onUsePeriodClick = useCallback(() => {
    const updateArray: FormUpdateAssociatonType[][] = [];

    chosenRows.forEach(row => {
      const isNewRow = row.id.includes(NEWLY_ADDED_ID_PREFIX_FOR_UPDATE);

      updateArray.push([
        {
          id: row.id,
          operation: isNewRow ? 'Add_Values' : 'Update_Values',
          associationName: 'Rader',
          values: {
            PERIODISERAD: TEDEKBokforderRadPERIODISERADEnum.JA,
            PERIODISERAD_FROM_DATUM: fromDateInputValue.value,
            PERIODISERAD_TOM_DATUM: toDateInputValue.value,
          },
        },
      ]);
    });

    formContextUpdate?.(updateArray);
  }, [chosenRows, formContextUpdate, fromDateInputValue, toDateInputValue]);

  const cancelAction = useCallback(() => {
    formContextDispatch?.({ type: 'CANCEL' });
    setOpen(false);
  }, [formContextDispatch, setOpen]);

  const saveAction = useCallback(() => {
    parentFormContextDispatch?.({
      type: 'UPDATE',
      value: formContext?.formState.values,
    });
    formContextDispatch?.({
      type: 'CANCEL',
    });
    setOpen(false);
  }, [
    formContext?.formState.values,
    formContextDispatch,
    parentFormContextDispatch,
    setOpen,
  ]);

  const cantEditPeriod = useCantEditPeriod();

  const clearButtonDisabledReason = useMemo(() => {
    if (cantEditPeriod) {
      return 'Du kan inte redigera bokföringsordern när den inte ligger hos dig';
    }

    return bokforderContext.bokforderVariant ===
      TEDEKBokforderVariantEnum.PERIODISERA
      ? 'Du kan inte rensa periodisering intervallet när du periodiserar en befintlig verifikation'
      : undefined;
  }, [bokforderContext.bokforderVariant, cantEditPeriod]);

  return (
    <EDDialog
      fullHeight={true}
      fullWidth={true}
      maxWidth="lg"
      open={props.open}
      onClose={() => props.setOpen(false)}
      onExited={() => {
        resetStates();
      }}
      footerActions={[
        {
          label: 'Avbryt',
          onClick: () => {
            if (formContext?.formState.formHasChanged) {
              setAreYouSureDialogOpen(true);
              setDialogYesCallback({ callback: cancelAction });
            } else {
              cancelAction();
            }
          },
        },
        {
          label: 'Använd',
          buttonProps: {
            disabled:
              !formContext?.formState.formHasChanged ||
              formContext.formState.validation.length !== 0 ||
              cantEditPeriod,
            disabledReason: !formContext?.formState.formHasChanged
              ? cantEditPeriod
                ? 'Du kan inte redigera bokföringsordern när den inte ligger hos dig'
                : 'Inga ändringar gjorda'
              : formContext.formState.validation.map(val => val.message),
          },
          onClick: () => saveAction(),
        },
      ]}
      title="Välj rader att periodisera"
    >
      <EDBox sx={{ height: '100%' }}>
        <BokforderPeriodDateHandler
          bokfDatumDisabled={formContext?.data.BOKF_DATUM ? true : false}
          availableRows={availableRows}
          chosenRows={chosenRows}
          clearButtonDisabledReason={clearButtonDisabledReason}
          disableClearButton={
            bokforderContext.bokforderVariant ===
              TEDEKBokforderVariantEnum.PERIODISERA || cantEditPeriod
          }
          fromDateInputValue={fromDateInputValue}
          infoDiffMoreThanOneYearDialogOpen={infoDiffMoreThanOneYearDialogOpen}
          infoSummeringDialogOpen={infoSummeringDialogOpen}
          onClearClick={() => {
            const deleteArray: FormUpdateAssociatonType[][] = [];

            chosenRows.forEach(row => {
              const isNewRow = row.id.includes(
                NEWLY_ADDED_ID_PREFIX_FOR_UPDATE
              );

              deleteArray.push([
                {
                  id: row.id,
                  operation: isNewRow ? 'Add_Values' : 'Update_Values',
                  associationName: 'Rader',
                  values: {
                    PERIODISERAD: TEDEKBokforderRadPERIODISERADEnum.NEJ,
                    PERIODISERAD_FROM_DATUM: '',
                    PERIODISERAD_TOM_DATUM: '',
                  },
                },
              ]);
            });

            formContext?.update(deleteArray);
          }}
          onDateBlur={onDateBlur}
          onUseClick={onUsePeriodClick}
          setChosenRows={setChosenRows}
          setFromDateInputValue={setFromDateInputValue}
          setInfoDiffMoreThanOneYearDialogOpen={
            setInfoDiffMoreThanOneYearDialogOpen
          }
          setInfoSummeringDialogOpen={setInfoSummeringDialogOpen}
          setToDateInputValue={setToDateInputValue}
          toDateInputValue={toDateInputValue}
        />
        <EDBox sx={{ height: 'calc(100% - 42px)' }}>
          <EDDataGrid
            columns={columns}
            disableColumnMenu
            rows={allFilledRows ?? []}
            onRowClick={(params, event) => {
              const canBeClicked = allFilledRows.some(
                row => row.id === params.id.toString()
              );

              if (canBeClicked) {
                const isChecked = chosenRows.some(
                  chosenRow => chosenRow.id === params.id.toString()
                );

                onCheckboxClick(params.id.toString())(!isChecked);
              }
            }}
            RowComponent={RowComponentWithTooltip}
            rowProps={{
              availableRows: allFilledRows,
              bokforderType: bokforderContext.bokforderVariant,
              getRowDisabledReason: getRowDisabledReason,
            }}
          />
        </EDBox>
      </EDBox>
      <BokforderAreYouSureDialog
        open={areYouSureDialogOpen}
        setOpen={setAreYouSureDialogOpen}
        title={'Är du säker?'}
        boldWarningText={'Dina ändringar kommer att gå förlorade'}
        warningText={'Är du säker på att du vill avbryta?'}
        noCallback={() => {
          setAreYouSureDialogOpen(false);
        }}
        yesCallback={dialogYesCallback.callback}
      />
    </EDDialog>
  );
};

export default BokforderPeriodDialog;
